import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const Publish = ({ 
  formData, 
  setFormData, 
  createPartialSale, 
  company, 
  user, 
  partialSaleQuestions 
}) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handlePublishToggle = (publish) => {
    setFormData({
      ...formData,
      publishOnSave: publish
    });
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setSubmitError('');
    
    const variables = {
      numberOfClients: parseInt(formData.numberOfClients) || 0,
      assetsUnderManagement: formData.assetsUnderManagement || 0,
      feeBasedRevenue: formData.feeBasedRevenue || 0,
      trailerRevenue: formData.trailerRevenue || 0,
      directRevenue: formData.directRevenue || 0,
      commissionRevenue: formData.commissionRevenue || 0,
      description: formData.description || '',
      questions: formData.questions || [],
      answers: formData.answers || [],
      states: formData.states || [],
      clientDemographicsDetails: formData.clientDemographicsDetails || '',
      servicesProvidedDetails: formData.servicesProvidedDetails || '',
      otherDetails: formData.otherDetails || '',
      askingPrice: formData.askingPrice || 0,
      segmentDetails: formData.segmentDetails || '',
      publishOnSave: !!formData.publishOnSave
    };

    createPartialSale({ variables })
      .then((result) => {
        console.log('Partial sale created successfully:', result);
        setSubmitSuccess(true);
        
        // Redirect after successful creation
        setTimeout(() => {
          window.location.href = '/succession/my_partial_sales';
        }, 2000);
      })
      .catch((error) => {
        console.error('Error creating partial sale:', error);
        setSubmitError(intl.formatMessage({ id: 'succession.partial_sales.publish.error_create_failed' }));
        setIsSubmitting(false);
      });
  };

  if (submitSuccess) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-succession-success-bg rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-succession-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-succession-success mb-2">
          <FormattedMessage id="succession.partial_sales.publish.success_title" />
        </h2>
        <p className="text-lg text-gray-600 mb-4">
          {formData.publishOnSave
            ? <FormattedMessage id="succession.partial_sales.publish.success_published" />
            : <FormattedMessage id="succession.partial_sales.publish.success_draft" />
          }
        </p>
        <p className="text-sm text-gray-500">
          <FormattedMessage id="succession.partial_sales.publish.success_redirect" />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.publish.title" />
        </h2>
        <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.publish.intro" />
        </p>
      </div>

      {/* Publishing Options */}
      <div className="space-y-4">
        <div 
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            formData.publishOnSave 
              ? 'border-succession-primary bg-succession-primary-bg' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={() => handlePublishToggle(true)}
        >
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="radio"
                checked={formData.publishOnSave}
                onChange={() => handlePublishToggle(true)}
                className="h-4 w-4 border-succession-border focus:ring-succession-primary"
                style={{
                  accentColor: 'var(--succession-primary)'
                }}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                <FormattedMessage id="succession.partial_sales.publish.publish_immediately_title" />
              </h3>
              <p className="text-sm mt-1 text-succession-text-secondary transition-colors duration-300">
                <FormattedMessage id="succession.partial_sales.publish.publish_immediately_desc" />
              </p>
              <div className="mt-2 text-sm text-succession-primary">
                <FormattedMessage id="succession.partial_sales.publish.publish_check_1" /><br/>
                <FormattedMessage id="succession.partial_sales.publish.publish_check_2" /><br/>
                <FormattedMessage id="succession.partial_sales.publish.publish_check_3" />
              </div>
            </div>
          </div>
        </div>

        <div 
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            !formData.publishOnSave 
              ? 'border-succession-primary bg-succession-primary-bg' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={() => handlePublishToggle(false)}
        >
          <div className="flex items-start space-x-3">
            <div className="flex items-center h-5">
              <input
                type="radio"
                checked={!formData.publishOnSave}
                onChange={() => handlePublishToggle(false)}
                className="h-4 w-4 border-succession-border focus:ring-succession-primary"
                style={{
                  accentColor: 'var(--succession-primary)'
                }}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                <FormattedMessage id="succession.partial_sales.publish.save_draft_title" />
              </h3>
              <p className="text-sm mt-1 text-succession-text-secondary transition-colors duration-300">
                <FormattedMessage id="succession.partial_sales.publish.save_draft_desc" />
              </p>
              <div className="mt-2 text-sm text-orange-600">
                <FormattedMessage id="succession.partial_sales.publish.draft_check_1" /><br/>
                <FormattedMessage id="succession.partial_sales.publish.draft_check_2" /><br/>
                <FormattedMessage id="succession.partial_sales.publish.draft_check_3" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="bg-succession-bg-secondary p-4 rounded-lg border border-succession-border">
        <h3 className="text-lg font-semibold mb-3 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.publish.what_happens_next_title" />
        </h3>
        <div className="space-y-2 text-sm text-succession-text-secondary transition-colors duration-300">
          {formData.publishOnSave ? (
            <>
              <p>• <FormattedMessage id="succession.partial_sales.publish.publish_next_1" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.publish_next_2" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.publish_next_3" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.publish_next_4" /></p>
            </>
          ) : (
            <>
              <p>• <FormattedMessage id="succession.partial_sales.publish.draft_next_1" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.draft_next_2" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.draft_next_3" /></p>
              <p>• <FormattedMessage id="succession.partial_sales.publish.draft_next_4" /></p>
            </>
          )}
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">
          <FormattedMessage id="succession.partial_sales.publish.important_considerations_title" />
        </h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <FormattedMessage id="succession.partial_sales.publish.consideration_1" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.publish.consideration_2" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.publish.consideration_3" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.publish.consideration_4" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.publish.consideration_5" /></li>
        </ul>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="bg-succession-error-bg border border-succession-error rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-succession-error mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-succession-error">{submitError}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-6">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-lg transition-colors ${
            isSubmitting
              ? 'bg-succession-bg-secondary text-succession-text-muted cursor-not-allowed border border-succession-border'
              : 'bg-succession-primary text-white hover:opacity-80 transition-all'
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {formData.publishOnSave ? <FormattedMessage id="succession.partial_sales.publish.submit_publishing" /> : <FormattedMessage id="succession.partial_sales.publish.submit_saving" />}
            </div>
          ) : (
            formData.publishOnSave ? <FormattedMessage id="succession.partial_sales.publish.submit_publish" /> : <FormattedMessage id="succession.partial_sales.publish.submit_save_draft" />
          )}
        </button>
      </div>
    </div>
  );
};

export default Publish;